const { batchIdToProductId } = require('./product-batch-util')
const { createBatchProductResolver } = require('../../batch/tools/batch-product-resolver')
const { listBatches } = require('../../batch/api/batch-list')
const keyBy = require('lodash/keyBy')

/**
 * Calculates total volume and price for a shipment.
 * For PSM tenant only: Uses manufacturer-specific product volumetrics when available,
 * and does not fall back to generic product volumetrics.
 * For other tenants: Uses generic product volumetrics only (manufacturer in batch ID is just metadata).
 * @param {Object} shipment - Shipment object with counts property
 * @param {Object} mainApi - Main API object with state.tenant and product.getByIds method
 * @returns {Promise<{totalVolume: number, totalPrice: number}>} Total volume and price
 */
const calculateShipmentVolumeAndPrice = async (shipment, mainApi) => {
  const tenant = mainApi && mainApi.state && mainApi.state.tenant
  // Only use manufacturer-specific products for PSM tenant (not sub-tenants like EPN, RMS, etc.)
  // Validate tenant is a string before calling toLowerCase to avoid runtime errors
  const useManufacturerSpecificProducts = tenant && typeof tenant === 'string' && tenant.toLowerCase() === 'psm'

  // Collect generic product IDs (always) plus, for PSM, the manufacturer-specific
  // product each batch points at via its Couch master-data `alias`.
  const productIds = new Set()
  const batchIdToManufacturerProductId = {}

  for (const batchId of Object.keys(shipment.counts)) {
    productIds.add(batchIdToProductId(batchId))
  }

  const allProductsDoc = await mainApi.product.getByIds([...productIds])
  const productsById = keyBy(allProductsDoc, '_id')

  if (useManufacturerSpecificProducts) {
    try {
      const batchIds = Object.keys(shipment.counts)
      const resolver = createBatchProductResolver({
        fetchBatchesByIds: (ids) => listBatches(mainApi.state, { ids })
      })
      // First pass (no product docs yet) to discover alias-derived ids...
      let resolved = await resolver.resolveMany({ batchIds })
      const resolvedIds = [...new Set(Object.values(resolved).filter(Boolean))]
      if (resolvedIds.length) {
        const resolvedProducts = await mainApi.product.getByIds(resolvedIds)
        Object.assign(productsById, keyBy(resolvedProducts, '_id'))
      }
      // ...then re-resolve with products loaded so the generic-parent guard applies.
      resolved = await resolver.resolveMany({ batchIds, productsById })
      Object.assign(batchIdToManufacturerProductId, resolved)
    } catch (e) {
      // Best-effort: with no batch docs we keep the generic-only behaviour.
    }
  }

  let totalVolume = 0
  let totalPrice = 0

  for (const batchId of Object.keys(shipment.counts)) {
    const { quantity } = shipment.counts[batchId]
    const genericProductId = batchIdToProductId(batchId)

    // Try manufacturer-specific product first (only for PSM tenant)
    const manufacturerProductId = useManufacturerSpecificProducts
      ? batchIdToManufacturerProductId[batchId]
      : null

    // For volume (PSM): prefer manufacturer-specific, and do not fall back to generic if manufacturer-specific has no volumetrics
    const manufacturerProduct = manufacturerProductId && productsById[manufacturerProductId]
      ? productsById[manufacturerProductId]
      : null
    const genericProduct = productsById[genericProductId]

    // unitVolume is considered present only if it's a positive number (> 0)
    const manufacturerUnitVolume = manufacturerProduct ? Number(manufacturerProduct.unitVolume) : 0

    const productForVolume = useManufacturerSpecificProducts
      ? ((manufacturerProduct && manufacturerUnitVolume > 0) ? manufacturerProduct : null)
      : genericProduct

    // IMPORTANT: Price must always come from generic product only (no manufacturer-specific pricing).
    // Preserve old falsy behavior: treat 0 as missing for unitPrice.
    const unitPrice = (genericProduct && genericProduct.unitPrice) ? genericProduct.unitPrice : 0

    if (genericProduct) {
      const unitVolume = (productForVolume && Number(productForVolume.unitVolume) > 0) ? Number(productForVolume.unitVolume) : 0
      totalVolume += quantity * unitVolume
      totalPrice += quantity * unitPrice
    } else {
      console.error(`Product not found for shipment ${shipment.id}, batchId: ${batchId}, tried: ${manufacturerProductId || 'N/A'} and ${genericProductId}`)
    }
  }
  return { totalVolume, totalPrice }
}
exports.calculateShipmentVolumeAndPrice = calculateShipmentVolumeAndPrice
